import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import * as Sentry from '@sentry/react';
import debug from 'debug';

// GraphQL
import { CREATE_NEW_IMAGE, DELETE_MANY_IMAGES } from '../gql/mutations/accidents';
import { NEW_IMAGE } from '../gql/fragments/image';

// Queue
import {
  getImageUploadQueue,
  removeImageUpload,
  incrementImageUploadRetry,
  updateImageUploadStatus,
  hasExceededMaxRetries,
  resetFailedImageUploads,
  QueuedImageUpload,
} from './offlineImageUploadQueue';

// Utils
import {
  getSpecificFileFromDirectory,
  checkFileInDirectory,
  deleteFilesFromDirectory,
} from '../utils/files';
import {
  generateImageNameWithROID,
  type CreateImageInput,
} from '../utils/image';
import { uploadToHetzner, type EntityType } from '../utils/storage';
import { runUploadPool, resolveConcurrency, withRetry } from '../utils/uploadPool';
import { Capacitor } from '@capacitor/core';
import { classifyOfflineError } from './classifyOfflineError';
import { isSessionChangedError, SessionChangedError } from '../gql/refreshFailure';
import { belongsToCurrentSession, currentQueueOwner, sessionLeftOperation } from './queueOwner';
import { esErrorTransitorioDeBackend, hasBackendErrorCode } from '../gql/retryableBackendCodes';
import { enSerieParaLaOrden } from './orderUploadChain';
import { mutateWithDeadline } from './mutationDeadline';
import { recordLostImage } from './lostImageNotices';
import { formatRoid } from '../utils/imageNaming';

/**
 * Maps an `OrderType` to the `EntityType` used as the storage key prefix.
 * Strict by design: only the OrderTypes that support image upload
 * ('accident', 'action', 'communication', 'routeSegment') are mapped
 * explicitly. Any other OrderType (surveillanceRoute, campaign, generic,
 * subOrder, multimedia, ...) throws so the op fails visibly via the sync
 * manager's error handling instead of silently uploading with an incorrect
 * entityType prefix.
 */
function deriveEntityType(op: QueuedImageUpload): EntityType {
  switch (op.orderType) {
    case 'accident':
      return 'accident';
    case 'action':
      return 'action';
    case 'communication':
      return 'communication';
    // Tramo del recorrido: fotos de la intervención leve. Prefijo propio para
    // no mezclarlas con las de actuación en el almacenamiento.
    case 'routeSegment':
      return 'segment';
    default:
      throw new Error(
        `deriveEntityType: unsupported OrderType for image upload: ${op.orderType}`,
      );
  }
}

const logSync = debug('rois:offline:image-sync');

if (import.meta.env.DEV) {
  if (typeof localStorage !== 'undefined' && !localStorage.debug) {
    // dev: activar el namespace por defecto
    localStorage.debug = 'rois:offline:*';
  }
}

/**
 * Cleanup post-migración Cloudinary→Hetzner (2026-05): se eliminó la subida
 * directa a Cloudinary (función `uploadBlobToCloudinary`) y la rama
 * `protocolVersion=1` del sync. Cualquier op encolada con v1 (improbable —
 * los clientes deberían haber flushado la cola al actualizar tras el deploy
 * Hetzner) se descarta vía `Sentry.captureMessage` para que sea visible y
 * no se silencie. La cola admite solo `protocolVersion=2` (Hetzner).
 */

/**
 * Sincroniza la cola de uploads de imágenes a Cloudinary + create record DB.
 *
 * **Invariante crítica**: para entradas con `sketchToRemoveId`, el DELETE del
 * sketch viejo SOLO se ejecuta si el CREATE del nuevo terminó OK. Si CREATE
 * falla en cualquier punto, DELETE nunca se ejecuta y el sketch viejo
 * permanece intacto en DB hasta que un sync nuevo tenga éxito.
 */
export interface SyncImageUploadOptions {
  /**
   * Si `true`, resetea status `failed` → `pending` antes de iterar. Usar SOLO
   * cuando el usuario dispara manualmente "Sincronizar Ahora" — permite
   * reintentar poison pills (p.ej. tras corregir el bug de cleanup OPFS).
   */
  resetFailed?: boolean;
}

// La fila por Order vive en `orderUploadChain.ts` (pura, testeable); se
// re-exporta para no mover a los llamadores.
export { enSerieParaLaOrden } from './orderUploadChain';

export async function syncImageUploadQueue(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  options?: SyncImageUploadOptions,
): Promise<{ synced: number; failed: number }> {
  if (options?.resetFailed) {
    const reset = await resetFailedImageUploads();
    if (reset > 0) {
      logSync('🔄 Reset %d operaciones failed → pending (sync manual)', reset);
    }
  }

  const fullQueue = await getImageUploadQueue();
  logSync(
    '📋 Cola actual: %d items (online=%s, resetFailed=%s)',
    fullQueue.length,
    navigator.onLine,
    !!options?.resetFailed,
  );

  // 🔴 Barrido de las `failed` SIN fichero (spec 2026-10-01-photo-outbox-identity
  // § 5.4). El drenado automático salta las `failed`, así que una sin fichero
  // solo se movía con «Reintentar»… para volver a fallar en 40 ms, siempre. No
  // pueden subir nunca: salen de la cola y dejan aviso con su orden.
  const lostIds = new Set<string>();
  for (const op of fullQueue) {
    if (op.status !== 'failed') continue;
    // Solo «no existe»: si OPFS falla por otra cosa, la entrada se queda.
    if (
      (await checkFileInDirectory(op.blobDirectory, op.blobFileName)) !==
      'missing'
    ) {
      continue;
    }
    await handleLostUpload(op);
    lostIds.add(op.id);
  }
  const queue = fullQueue.filter((op) => !lostIds.has(op.id));
  if (queue.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;
  const startedAt = Date.now();

  // ── Progreso y concurrencia (outbox de fotos, 2026-08) ────────────────────
  // Antes esto era un `for` secuencial. Con 20 fotos en la cola eso es una
  // detrás de otra y sin ninguna señal hacia la interfaz: el operario no sabe
  // si queda mucho o si se ha roto algo.
  //
  // `stopped` sustituye a los `break` del bucle: con varias subidas en vuelo no
  // se puede «romper», así que las que aún no han empezado se saltan.
  let stopped = false;
  let done = 0;
  /** Las que ni se intentaron (se perdió la red a mitad, o caducó la sesión). */
  const saltadas = new Set<string>();

  const owner = currentQueueOwner();
  // Lo de otro usuario o tenant se aparca igual que las `failed`: no se toca
  // (spec 2026-10-01 § 6.2).
  const eligible = queue.filter(
    (op) => op.status !== 'failed' && belongsToCurrentSession(op.owner, owner),
  );
  const skippedFailed = queue.filter((op) => op.status === 'failed').length;
  const skippedForeign = queue.filter(
    (op) => op.status !== 'failed' && !belongsToCurrentSession(op.owner, owner),
  ).length;
  if (skippedFailed > 0) {
    logSync('⏭️  %d op(s) con status=failed se saltan', skippedFailed);
  }
  if (skippedForeign > 0) {
    logSync('⏭️  %d op(s) de otro usuario o tenant se saltan', skippedForeign);
  }
  if (eligible.length === 0) return { synced: 0, failed: 0 };

  const emitProgress = () => {
    window.dispatchEvent(
      new CustomEvent('offline-image-progress', {
        detail: { done, total: eligible.length },
      }),
    );
  };
  emitProgress();

  const processQueuedUpload = async (op: QueuedImageUpload): Promise<void> => {
    // Las que se saltan NO cuentan como hechas: si se pierde la red a mitad,
    // el pool sigue llamando al worker para las que faltan y todas volvían por
    // `onSettled`, así que el contador llegaba a «20 de 20» y el banner se
    // retiraba sin haber subido nada. `saltada` lo distingue.
    if (stopped) {
      saltadas.add(op.id);
      return;
    }
    if (!navigator.onLine) {
      logSync('⏸️  Offline detectado mid-loop, deteniendo');
      stopped = true;
      saltadas.add(op.id);
      return;
    }
    // 🔴 `eligible` se filtró con el dueño del ARRANQUE, pero la sesión puede
    // cambiar a mitad del lote (switchTenant sin recarga, logout + login de
    // otro) y lo que queda saldría con el token nuevo. Se mira la sesión de
    // AHORA antes de empezar cada subida y, si ya no es la suya, se para el
    // lote sin gastar intentos ni tocar la operación.
    if (!belongsToCurrentSession(op.owner, currentQueueOwner())) {
      logSync('🔀 La sesión cambió a mitad del lote, deteniendo');
      stopped = true;
      saltadas.add(op.id);
      return;
    }
    // Tras MAX_RETRIES reintentos fallidos → `failed`: el auto-sync deja de
    // intentarlo y el banner lo SURFACEA en NARANJA ("no se pudieron subir ·
    // Reintentar"), en vez de quedar como "pendiente" azul ETERNO que reintenta
    // en silencio y confunde ("Sincronizar no hace nada" — feedback usuario
    // 2026-07-31, con un croquis que no sube por CORS/red al storage). NO es
    // callejón sin salida: `resetFailedImageUploads` (botón Reintentar) resetea
    // `retryCount` a 0 → reintento REAL. El bug original ("1 pendiente imposible
    // de quitar") era que ese reset NO tocaba `retryCount` y se re-marcaba
    // failed al instante; eso ya está arreglado (ver la cola).
    if (hasExceededMaxRetries(op)) {
      logSync(
        '💀 %s — MAX_RETRIES (%d) → failed (recuperable con Reintentar)',
        op.id,
        op.retryCount,
      );
      await markAsFailed(op, 'Máximo de reintentos alcanzado');
      failed++;
      return;
    }

    try {
      logSync(
        '🚀 Procesando %s kind=%s orderId=%s sketchToRemove=%s blob=%s/%s',
        op.id,
        op.kind,
        op.orderId,
        op.sketchToRemoveId ?? 'none',
        op.blobDirectory,
        op.blobFileName,
      );
      await updateImageUploadStatus(op.id, 'syncing');
      // El reintento DENTRO de esta pasada vive en `processOne`, paso a paso.
      // Envolver aquí el conjunto era caro: un fallo transitorio del
      // `createImage` (400 bytes) re-subía la foto ENTERA (~1 MB) y dejaba un
      // blob huérfano en Hetzner — justo el desperdicio que este trabajo
      // intenta evitar en cobertura mala.
      await processOne(apolloClient, op);
      logSync('✅ %s sincronizado correctamente', op.id);
      synced++;
    } catch (error: any) {
      // Sin fichero no hay nada que subir, ni ahora ni con «Reintentar»: fuera
      // de la cola y aviso fijo con su orden. Ni toast ni cuenta de fallidas.
      if (error instanceof LostImageBlobError) {
        logSync('🕳️  %s — sin fichero en la bandeja, fuera de la cola', op.id);
        await handleLostUpload(op);
        return;
      }
      // Su orden se borró mientras esperaba: tampoco subirá nunca. Fuera de la
      // cola, sin su fichero y con un aviso que lo dice. Antes se quedaba en
      // «no se pudieron subir · Reintentar» para siempre.
      if (error instanceof ImageOrderGoneError) {
        logSync('🗑️  %s — su orden ya no existe, fuera de la cola', op.id);
        await handleOrderGoneUpload(op);
        return;
      }
      logSync(
        '❌ %s falló — %s (cloudinaryStatus=%s httpStatus=%s graphQLErrors=%s)',
        op.id,
        error?.message ?? error,
        error?.cloudinaryStatus ?? 'n/a',
        error?.httpStatus ?? 'n/a',
        error?.graphQLErrors?.length ?? 0,
      );
      // La sesión cambió con la subida en curso (spec § 12.1, § 12.11): igual
      // que el corte de arriba, sin gastar intento ni tocar la operación, y se
      // para el lote: lo que queda es de la sesión anterior. Lo mismo si su
      // dueño cerró sesión a mitad (los pasos de una subida son varias
      // peticiones): el fallo es del logout, no de la foto (spec 2026-10-12 § 10).
      if (isSessionChangedError(error) || sessionLeftOperation(op.owner)) {
        logSync('🔀 %s — la sesión cambió con la subida en curso, deteniendo', op.id);
        await updateImageUploadStatus(op.id, 'pending', error?.message);
        stopped = true;
        saltadas.add(op.id);
        return;
      }
      // Sin servidor (spec 2026-10-09 § 9): la petición ni salió —
      // `mutateWithDeadline` ya sabía que el backend no contesta—. No es un
      // intento: gastarlo aparcaba la foto a los 5 con el servidor solo un rato
      // caído. Se para el lote: lo que queda fallaría igual.
      if (error?.name === 'BackendUnreachableError') {
        logSync('⏸️ %s — sin servidor: no se envió, no gasta intento', op.id);
        await updateImageUploadStatus(op.id, 'pending', error?.message);
        stopped = true;
        saltadas.add(op.id);
        return;
      }
      // 🛡️ FIX pérdida de datos: un error de auth (token caducado al llamar
      // CREATE_NEW_IMAGE) es un GraphQLError → antes caía en isGraphQLValidationError
      // → markAsFailed (terminal, requería "Sincronizar Ahora" manual). NO es
      // terminal: mantener reintentable + parar para re-login (el resto falla igual).
      if (classifyOfflineError(error) === 'auth') {
        logSync('🔐 %s — error de auth, mantengo reintentable y paro para re-login', op.id);
        await incrementImageUploadRetry(op.id);
        await updateImageUploadStatus(op.id, 'pending', error?.message);
        window.dispatchEvent(
          new CustomEvent('offline-sync-auth-required', {
            detail: {
              message:
                'Sesión caducada. Vuelve a iniciar sesión para sincronizar las fotos guardadas sin conexión.',
            },
          })
        );
        failed++;
        stopped = true;
        return;
      }
      const isCloudinaryClientError =
        typeof error?.cloudinaryStatus === 'number' &&
        error.cloudinaryStatus >= 400 &&
        error.cloudinaryStatus < 500;
      // HetznerUploadError carries `httpStatus` (not `cloudinaryStatus`). A 4xx
      // on the presigned PUT (403 expired/invalid signature, 400 malformed) is
      // terminal — the token won't become valid on retry. 5xx/network
      // (httpStatus undefined or >=500) stays retryable.
      const isHetznerClientError =
        error?.name === 'HetznerUploadError' &&
        typeof error?.httpStatus === 'number' &&
        error.httpStatus >= 400 &&
        error.httpStatus < 500;
      // 🔴 Abortos TRANSITORIOS del backend (incidente 2026-08-10, subida de 30
      // fotos). `SAVE_TX_CONFLICT` (P2034) llega como GraphQLError, así que caía
      // en `isGraphQLValidationError` y se marcaba VENENO PERMANENTE: la foto
      // moría con «no se pudieron subir» y había que pulsar Reintentar a mano.
      //
      // MongoDB clasifica el write conflict como `TransientTransactionError` y
      // su documentación es explícita: nunca dejar que un WriteConflict falle
      // una operación de forma permanente. El proyecto ya lo tenía escrito en
      // `RETRYABLE_BACKEND_CODES` para el errorLink; esta cola lo ignoraba.
      if (esErrorTransitorioDeBackend(error)) {
        logSync(
          '🔁 %s — aborto transitorio del backend, sigue reintentable',
          op.id,
        );
        await incrementImageUploadRetry(op.id);
        await updateImageUploadStatus(op.id, 'pending', error?.message);
        failed++;
        return;
      }

      const isGraphQLValidationError =
        (Array.isArray(error?.graphQLErrors) && error.graphQLErrors.length > 0) ||
        error?.networkError?.statusCode === 400 ||
        (Array.isArray(error?.networkError?.result?.errors) &&
          error.networkError.result.errors.length > 0);

      if (
        isCloudinaryClientError ||
        isHetznerClientError ||
        isGraphQLValidationError
      ) {
        Sentry.captureException(error);
        await markAsFailed(op, error?.message ?? 'Error permanente');
        failed++;
      } else {
        await incrementImageUploadRetry(op.id);
        await updateImageUploadStatus(op.id, 'pending', error?.message);
        failed++;
      }
    }
  };

  await runUploadPool(eligible, processQueuedUpload, {
    concurrency: resolveConcurrency(),
    // 0 aquí a propósito: `processQueuedUpload` NUNCA lanza (se traga todo para
    // llevar la contabilidad persistida de la cola), así que el reintento del
    // pool no se dispararía nunca. El reintento real está una capa más adentro,
    // en el `withRetry` que envuelve a `processOne`.
    retries: 0,
    onSettled: (index) => {
      // Solo cuenta lo que se llegó a intentar. Ver `saltadas`.
      if (saltadas.has(eligible[index]?.id)) return;
      done += 1;
      emitProgress();
    },
  });

  // Lote cortado (sin red, o su dueño cerró sesión): las saltadas no cuentan,
  // así que el progreso nunca llega al total y el aviso se quedaba en «Subiendo
  // fotos · 0 de 2» sin subir nada (medido en tablet en la pantalla de login,
  // spec 2026-10-12 § 10). Se retira el progreso: el aviso vuelve a contar lo
  // pendiente, que es lo que hay.
  if (saltadas.size > 0) {
    window.dispatchEvent(
      new CustomEvent('offline-image-progress', { detail: { done: 0, total: 0 } }),
    );
  }

  // Medición: es el dato que decide si hace falta la Fase 2 (subidor nativo en
  // segundo plano). Disparadores del spec: >10 % de lotes interrumpidos o p95
  // de un lote de 20 por encima de 5 minutos.
  Sentry.addBreadcrumb({
    category: 'image-upload-batch',
    level: 'info',
    message: 'lote de subida de imágenes terminado',
    data: {
      total: eligible.length,
      synced,
      failed,
      durationMs: Date.now() - startedAt,
      concurrency: resolveConcurrency(),
      effectiveType:
        (navigator as Navigator & { connection?: { effectiveType?: string } })
          .connection?.effectiveType ?? 'unknown',
      native: Capacitor.isNativePlatform(),
      stoppedEarly: stopped,
    },
  });

  return { synced, failed };
}

async function processOne(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  op: QueuedImageUpload
): Promise<void> {
  // Fallos con los que reintentar no sirve de nada: se cortan con un error
  // que `withRetry` no reintenta y que la cola sabe tratar.
  // - Su dueño cerró sesión a mitad (una subida son varias peticiones): la
  //   siguiente saldría sin sesión. Como «la sesión cambió»: pendiente y sin
  //   gastar intento (spec 2026-10-12 § 10).
  // - La orden de destino ya no existe: no subirá nunca (spec
  //   2026-10-12-image-order-gone).
  const failFast = async <T>(step: () => Promise<T>): Promise<T> => {
    try {
      return await step();
    } catch (error) {
      if (sessionLeftOperation(op.owner)) throw new SessionChangedError();
      if (hasBackendErrorCode(error, IMAGE_ORDER_NOT_FOUND)) throw new ImageOrderGoneError();
      throw error;
    }
  };

  // ── Paso 1: leer blob desde OPFS ──
  logSync('  ↳ leyendo blob OPFS: %s/%s', op.blobDirectory, op.blobFileName);
  const fileResult = await getSpecificFileFromDirectory(
    op.blobDirectory,
    op.blobFileName
  );

  if (!fileResult) {
    // Blob desaparecido (limpieza manual u OPFS purgado). Sin fuente, no se
    // puede recuperar la subida. Marcar failed para que el usuario lo sepa.
    logSync(
      '  ❗ blob NO encontrado en OPFS — el directorio %s puede haber sido limpiado por removeNew*Storage()',
      op.blobDirectory,
    );
    // Solo es «perdida» si el fichero NO EXISTE. Si no se pudo leer por otra
    // causa, es transitorio: sigue en la cola y se reintenta.
    if (
      (await checkFileInDirectory(op.blobDirectory, op.blobFileName)) ===
      'missing'
    ) {
      throw new LostImageBlobError();
    }
    throw new Error('No se pudo leer el fichero de la bandeja (OPFS)');
  }

  const blob = new Blob([fileResult.file], {
    type: fileResult.file.type || 'image/png',
  });
  logSync('  ✓ blob leído (%d bytes)', blob.size);

  // ── Paso 2: nombre estandarizado ROID (necesario antes del upload Hetzner) ──
  const name =
    op.kind === 'sketch'
      ? generateImageNameWithROID({
          ...op.orderMetadata,
          imageType: 'canvas',
        })
      : generateImageNameWithROID({
          ...op.orderMetadata,
          imageType: 'photo',
          sequence: op.photoSequence ?? 1,
        });

  // ── Paso 3: subir el blob a Hetzner (protocolVersion=2 obligatorio) ──
  // El path Cloudinary directo (protocol v1) se eliminó tras la migración
  // 2026-05. Ops v1 residuales en la cola → dead-letter visible en Sentry
  // (no se reintentan, no se ejecutan: hacerlo subiría a Cloudinary y
  // re-saturaría storage que justo acabamos de liberar).
  const protocol = op.protocolVersion ?? 1;
  if (protocol !== 2) {
    Sentry.captureMessage(
      `[offline-sync] op con protocolVersion=${protocol} descartada ` +
        `(legacy Cloudinary eliminado). orderId=${op.orderId} kind=${op.kind}`,
      'warning',
    );
    const err: any = new Error(
      `Protocol v${protocol} no soportado (legacy Cloudinary eliminado)`,
    );
    err.cloudinaryStatus = 410; // gone — poison pill, no reintentar
    throw err;
  }
  logSync('  ↳ requestUploadToken + PUT Hetzner (protocol v2)');
  // Reintento acotado A ESTE PASO. Es el caro (los bytes de la foto), así que
  // se reintenta solo si falla ÉL — no arrastra al `createImage` de abajo ni
  // al revés. `withRetry` no reintenta los 4xx: un token caducado o una firma
  // inválida no mejoran insistiendo.
  const { storageKey } = await withRetry(() =>
    failFast(() =>
      uploadToHetzner(blob, {
        orderId: op.orderId,
        entityType: deriveEntityType(op),
        roidName: name,
        contentType: blob.type || 'image/jpeg',
      }),
    ),
  );
  logSync('  ✓ Hetzner OK storageKey=%s', storageKey);
  const createInput: CreateImageInput = {
    name,
    creatingDate: new Date().toISOString(),
    storageKey,
    orderId: op.orderId,
    sketchSvg: op.sketchData?.svgContent ?? null,
    sketchFabricJson: op.sketchData?.fabricJson ?? null,
    // GAP 8: id estable de esta op → si el navegador se cierra entre el
    // createImage de abajo y removeImageUpload(op.id), el retry al reabrir llega
    // con el MISMO clientOpId y el backend devuelve la Image ya creada (no duplica).
    clientOpId: op.id,
  };

  // ── Paso 4: crear Image record en DB ──
  //
  // 🔴 EN SERIE POR ORDEN, a diferencia del PUT de arriba.
  //
  // `createImage` con `clientOpId` (siempre, en este camino) corre dentro de
  // una `$transaction` interactiva que hace `orders: { connect: { id } }`, o
  // sea que TODAS las fotos de una actuación escriben el MISMO documento
  // `Order.imageIDs`. Lanzarlas en paralelo convierte ese documento en lo que
  // la documentación de MongoDB llama un «conflict magnet»: WiredTiger usa
  // control de concurrencia optimista, detecta el choque y aborta todas menos
  // una con WriteConflict (P2034 → `SAVE_TX_CONFLICT`).
  //
  // Medido en vivo con 30 fotos: decenas de `createImage` en rojo y 2 fotos
  // que se quedaron sin subir.
  //
  // Serializar SOLO este paso no cuesta apenas: son ~250 ms por foto (7 s con
  // 30) frente a los minutos que tarda la subida, que es lo que de verdad
  // interesa paralelizar porque depende del ancho de banda, no de la BD.
  //
  // NO se agrupan en una sola mutation a propósito: perderíamos el reintento
  // por foto, que es justo lo que hace que un fallo suelto no arrastre al lote.
  logSync('  ↳ CREATE_NEW_IMAGE name=%s', name);
  // Reintento acotado a la mutation: el blob YA está en Hetzner, así que un
  // tropiezo aquí no debe volver a subirlo. El `clientOpId` estable hace que
  // un reintento devuelva la Image ya creada en vez de duplicarla.
  //
  // 🔴 Con plazo (`mutateWithDeadline`): sin él, un alta sin respuesta retenía
  // la fila de esta orden —las siguientes fotos esperaban detrás— y la pantalla
  // encendida, sin fin. Al vencer falla como fallo de red: `withRetry` lo
  // reintenta y, si se agotan, la foto vuelve a `pending` para la próxima
  // pasada. El `clientOpId` hace seguro reintentar aunque el servidor llegara a
  // crearla.
  await withRetry(() =>
    failFast(() => enSerieParaLaOrden(op.orderId, () =>
    mutateWithDeadline(apolloClient, {
    mutation: CREATE_NEW_IMAGE,
    // La cola de fotos ya es su propio buzón: si pasara por la cola simple,
    // un fallo de red dejaría una copia duplicada.
    context: { skipOfflineQueue: true },
    variables: {
      input: createInput,
    },
    update(cache, { data }: any) {
      const createdImage = data?.createImage;
      if (!createdImage) return;
      const ref = cache.writeFragment({
        data: createdImage,
        fragment: NEW_IMAGE,
      });

      // Listado raíz de multimedia.
      cache.modify({
        fields: {
          images(existingImages: readonly any[] = []) {
            return [...existingImages, ref];
          },
        },
      });

      // 🔴 Y la ORDEN, que es lo que lee el resumen.
      //
      // Antes solo se tocaba el campo `images` de la RAÍZ (un `cache.modify`
      // sin `id` modifica ROOT_QUERY). La Order cacheada nunca se enteraba, así
      // que al terminar de subir 31 fotos el resumen seguía enseñando las 3 que
      // ya estaban al abrirlo: parecía que la sincronización no había hecho
      // nada. Visto en vivo el 2026-08-10.
      cache.modify({
        id: cache.identify({ __typename: 'Order', id: op.orderId }),
        fields: {
          imageIDs(actuales: unknown) {
            const lista = Array.isArray(actuales)
              ? (actuales as string[])
              : [];
            return lista.includes(createdImage.id)
              ? lista
              : [...lista, createdImage.id];
          },
          images(actuales: unknown) {
            const lista = Array.isArray(actuales) ? (actuales as any[]) : [];
            return [...lista, ref];
          },
        },
      });
    },
    }),
    )),
  );

  logSync('  ✓ CREATE_NEW_IMAGE OK');

  // ── Paso 5: SOLO si todo lo anterior fue OK → borrar sketch viejo ──
  if (op.sketchToRemoveId) {
    logSync('  ↳ DELETE_MANY_IMAGES old sketch=%s', op.sketchToRemoveId);
    await apolloClient.mutate({
      mutation: DELETE_MANY_IMAGES,
      variables: { ids: [op.sketchToRemoveId] },
      update(cache, { data }: any) {
        const deleted = data?.deleteManyImagesByIds;
        if (!Array.isArray(deleted)) return;
        deleted.forEach((image: { id: string }) => {
          cache.modify({
            id: cache.identify({ __typename: 'Image', id: image.id }),
            fields: {
              images(_: any, { DELETE }: any) {
                return DELETE;
              },
            },
          });
        });
      },
    });
  }

  // ── Paso 6: limpiar cola y OPFS ──
  // Primero la cola y luego el fichero: si la app muere entre los dos, queda un
  // fichero huérfano (inofensivo). Al revés quedaba una entrada sin fichero que
  // la pasada siguiente daba por perdida, y el operario repetía una foto que
  // ya estaba subida.
  await removeImageUpload(op.id);
  logSync('  ✓ removed from queue %s', op.id);
  try {
    await deleteFilesFromDirectory(op.blobDirectory, op.blobFileName);
    logSync('  ✓ blob OPFS eliminado %s', op.blobFileName);
  } catch (err) {
    logSync('  ⚠️  falla al limpiar blob OPFS (no bloquea): %O', err);
  }
}

/**
 * Lo que responde el servidor al alta de una imagen cuya orden de destino no
 * existe en el tenant (`rois-back` `image.service.ts`, `businessBadRequest`).
 * Solo este código saca una foto de la cola: cualquier otro error la deja en
 * «Reintentar», porque puede tener arreglo.
 */
export const IMAGE_ORDER_NOT_FOUND = 'IMAGE_ORDER_NOT_FOUND';

/**
 * La orden de destino ya no existe: la imagen no podrá subir nunca. El 404 es
 * para `withRetry`, que no reintenta los 4xx.
 */
export class ImageOrderGoneError extends Error {
  readonly httpStatus = 404;
  constructor() {
    super('La orden de destino de la imagen ya no existe');
    this.name = 'ImageOrderGoneError';
  }
}

/**
 * Imagen de una orden borrada: fuera de la cola, sin su fichero, aviso fijo con
 * el ROID y mensaje a Sentry (con la orden en el texto, como el de las
 * perdidas, para que el Dedupe del SDK no las junte).
 */
async function handleOrderGoneUpload(op: QueuedImageUpload): Promise<void> {
  await removeImageUpload(op.id);
  try {
    await deleteFilesFromDirectory(op.blobDirectory, op.blobFileName);
  } catch (err) {
    logSync('  ⚠️  falla al limpiar blob OPFS (no bloquea): %O', err);
  }
  await recordLostImage(op, 'order-gone');
  const roid = formatRoid(op.orderMetadata ?? {});
  Sentry.captureMessage(
    `[offline-sync] imagen descartada: su orden ya no existe (${roid} n.º ${op.photoSequence ?? '-'})`,
    {
      level: 'warning',
      fingerprint: ['offline-sync-image-order-gone'],
      tags: { orderType: op.orderType, kind: op.kind },
      extra: {
        orderId: op.orderId,
        roid,
        photoSequence: op.photoSequence,
        ageMs: Date.now() - op.timestamp,
      },
    },
  );
}

/**
 * El fichero de una entrada de la bandeja ya no existe: no puede subir nunca.
 * Nombre propio para que `processQueuedUpload` lo distinga antes que cualquier
 * otra clasificación.
 */
export class LostImageBlobError extends Error {
  constructor() {
    super('Blob no encontrado en OPFS');
    this.name = 'LostImageBlobError';
  }
}

/**
 * Entrada sin fichero: fuera de la cola, aviso fijo para el operario y mensaje
 * a Sentry CON la orden y la secuencia — el error de antes no las llevaba y no
 * hubo forma de saber qué fotos de la tablet Tram 2 se habían perdido.
 */
async function handleLostUpload(op: QueuedImageUpload): Promise<void> {
  await removeImageUpload(op.id);
  await recordLostImage(op);
  const roid = formatRoid(op.orderMetadata ?? {});
  // La orden y el número VAN EN EL MENSAJE: la integración Dedupe del SDK
  // descarta un mensaje idéntico al anterior aunque cambien los `extra`, y dos
  // fotos perdidas de la misma orden llegaban como UN evento (medido en la
  // tablet, 5-oct). El `fingerprint` fijo las agrupa igualmente en un issue.
  Sentry.captureMessage(
    `[offline-sync] imagen perdida: su fichero ya no está en la bandeja (${roid} n.º ${op.photoSequence ?? '-'})`,
    {
      level: 'error',
      fingerprint: ['offline-sync-lost-image'],
      tags: { orderType: op.orderType, kind: op.kind },
      extra: {
        orderId: op.orderId,
        roid,
        photoSequence: op.photoSequence,
        blobFileName: op.blobFileName,
        status: op.status,
        retryCount: op.retryCount,
        ageMs: Date.now() - op.timestamp,
      },
    },
  );
}

async function markAsFailed(
  op: QueuedImageUpload,
  message: string
): Promise<void> {
  await updateImageUploadStatus(op.id, 'failed', message);
  window.dispatchEvent(
    new CustomEvent('offline-sync-error', {
      detail: {
        message:
          op.kind === 'sketch'
            ? 'No se pudo sincronizar un boceto. Revisa los pendientes.'
            : 'No se pudo sincronizar una imagen. Revisa los pendientes.',
      },
    })
  );
}
